import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { setAppLocales, allLocales } from '@fs/zion-locale'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { Root as IdentSubdomainRoot } from '@fs/identity.js'
import ZionRoot, { FrontierBaseProviders } from '@fs/zion-root'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

// We can't use ZionRoot on ident subdomain (the provider sign up flow) because it will make a lot of network requests that will fail
const Root = window.location.hostname.includes('ident') ? IdentSubdomainRoot : ZionRoot

const header = {
  signedIn: false, // this is actually ignored by Root right now but providing it in case it's used in the future
  showMobileDrawer: false,
  showNavLinksDesktop: false,
  showFHCLocations: false,
  showUserOrAuth: false,
  showHelpOverlay: false,
}
const footer = { hidden: true }

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      // help avoid FS quota issues
      sampleRate: 0.1,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(allLocales)

  return (
    <Router basename={base}>
      <Root analytics header={header} footer={footer}>
        <App />
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
